import { createTheme, type MantineColorsTuple } from '@mantine/core';

// Zebra Stock theme (BUILD_SPEC.md §11): a calm, industrial tool used at a gatehouse
// or plant office — legibility and large touch targets over decoration. A restrained
// graphite neutral carries most of the UI; amber is reserved for status/attention
// (variance, warnings), never used as the primary action color.
const graphite: MantineColorsTuple = [
  '#f2f4f5',
  '#e2e6e8',
  '#c9d0d4',
  '#adb7bd',
  '#94a1a8',
  '#7c8b93',
  '#66747c',
  '#4f5b62',
  '#3a4348',
  '#252b2e',
];

// `amber` doubles as the brand amber below — its shade-5/6 vivids (#f5ac16 /
// #d99400) already sit close to the logo's amber stripe (~#E8A203), so it is
// reused rather than duplicated as a `brandAmber` tuple (branded-neutral
// ruling, 2026-08-24). It predates the ruling and is already load-bearing in
// 30+ call sites as color="amber", so it keeps its existing name.
const amber: MantineColorsTuple = [
  '#fff8e8',
  '#ffedc2',
  '#ffdf96',
  '#ffd066',
  '#ffc23c',
  '#f5ac16',
  '#d99400',
  '#b87c00',
  '#976600',
  '#7a5300',
];

// Brand accent palettes, derived from the Zebra Renewables logo's chevron
// stripes (branded-neutral ruling, 2026-08-24: graphite/amber above stay the
// working-surface neutrals; these stripe colors are used ONLY functionally —
// status/badges/accents — never as page decoration). Semantic mapping for
// later goals to follow consistently:
//   brandGreen  (~#2E7D32 forest green) — posted / success
//   amber       (~#E8A203, existing tuple above) — draft / pending
//   brandOrange (~#F28C00) — warning / variance
//   brandRed    (~#FF3B00 vermillion) — error / blocked
const brandGreen: MantineColorsTuple = [
  '#eaf7ec',
  '#c9ecd0',
  '#a3ddac',
  '#7cce87',
  '#5abf68',
  '#3fae4e',
  '#2e7d32',
  '#276b2b',
  '#1f5723',
  '#17431b',
];

const brandOrange: MantineColorsTuple = [
  '#fff4e0',
  '#ffe2b0',
  '#ffcd7d',
  '#ffb84a',
  '#ffa61f',
  '#fa9700',
  '#f28c00',
  '#cc7400',
  '#a65e00',
  '#804900',
];

const brandRed: MantineColorsTuple = [
  '#ffece6',
  '#ffd0c0',
  '#ffb096',
  '#ff8f6c',
  '#ff6f47',
  '#ff5522',
  '#ff3b00',
  '#d63200',
  '#ad2900',
  '#852000',
];

export const theme = createTheme({
  primaryColor: 'graphite',
  colors: { graphite, amber, brandGreen, brandOrange, brandRed },
  primaryShade: { light: 7, dark: 4 },
  // Small controls (buttons, inputs, badges) keep the tighter 'md' (8px) radius;
  // larger surfaces (Card/Paper/Modal below) opt into 'lg' (16px) individually —
  // a bigger radius on a big surface reads as "considered", the same on a small
  // control reads as a bubble button (design-uplift goal, 2026-08-25).
  defaultRadius: 'md',
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  // Confident type scale (design-uplift goal): heavier, slightly tighter headings
  // than Mantine's defaults — the flat, same-weight headings across every page
  // were part of what read as "basic". Body text/fontSizes are left at Mantine's
  // defaults; only the heading scale changes.
  headings: {
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '2.25rem', lineHeight: '1.2', fontWeight: '800' },
      h2: { fontSize: '1.625rem', lineHeight: '1.25', fontWeight: '700' },
      h3: { fontSize: '1.25rem', lineHeight: '1.35', fontWeight: '700' },
      h4: { fontSize: '1.0625rem', lineHeight: '1.4', fontWeight: '600' },
    },
  },
  // Large touch targets everywhere (§11 — usable at a tablet gatehouse) rather than
  // Mantine's compact defaults.
  components: {
    Button: {
      defaultProps: { size: 'md' },
      styles: { root: { fontWeight: 600 } },
    },
    ActionIcon: { defaultProps: { size: 'lg' } },
    TextInput: {
      defaultProps: { size: 'md' },
      // Focus border in the brand amber rather than the graphite primary — an
      // interaction affordance, not page decoration, so it doesn't trip the
      // branded-neutral ruling (2026-08-24: stripe colors stay functional-only
      // — amber = draft/pending; a focus ring is exactly that kind of
      // "temporary attention" state). `--input-bd-focus` is a real Mantine CSS
      // variable (verified against the installed package's compiled
      // stylesheet/Input.cjs) already consumed by its own `:focus`/`:focus-within`
      // rule, so this needs no custom pseudo-selector CSS of its own.
      vars: () => ({ wrapper: { '--input-bd-focus': 'var(--mantine-color-amber-6)' } }),
    },
    Select: {
      defaultProps: { size: 'md' },
      vars: () => ({ wrapper: { '--input-bd-focus': 'var(--mantine-color-amber-6)' } }),
    },
    NavLink: { defaultProps: { py: 'sm' } },
    // Card/Paper (design-uplift goal, leverage point 1): the single biggest lever
    // for "looks basic" — every stat tile, register row-detail panel and form
    // section is a Paper. A larger radius + a very subtle default shadow (most
    // callers already pass withBorder explicitly; shadow stacks with a border
    // the way Linear/Stripe panels do — border for edge definition, shadow for
    // depth) turns flat bordered boxes into surfaces that sit on the page rather
    // than sit flush with it. defaultProps only fill in what a caller didn't
    // already set, so no existing withBorder/shadow/radius override is disturbed.
    Card: { defaultProps: { radius: 'lg', shadow: 'xs' } },
    Paper: { defaultProps: { radius: 'lg', shadow: 'xs' } },
    Modal: {
      defaultProps: {
        radius: 'lg',
        overlayProps: { backgroundOpacity: 0.55, blur: 3 },
      },
      styles: { title: { fontWeight: 700 } },
    },
    Tabs: {
      styles: { tab: { fontWeight: 600 } },
    },
    Table: {
      // highlightOnHover/spacing are real props (not custom CSS) — row hover and
      // a slightly airier default density than Mantine's cramped 7px default.
      defaultProps: { highlightOnHover: true, verticalSpacing: 'sm', horizontalSpacing: 'md' },
      styles: {
        th: {
          fontWeight: 700,
          fontSize: 'var(--mantine-font-size-xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.02em',
          color: 'var(--mantine-color-dimmed)',
        },
      },
    },
    Badge: { defaultProps: { variant: 'light', radius: 'sm' } },
    Tooltip: { defaultProps: { withArrow: true, radius: 'sm', openDelay: 200 } },
  },
});
